import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { RequirementItem } from 'components/DynamicFormFields';
import { t } from 'i18next';
import { FormProvider, Form } from 'react-hook-form';
import { useAssessment } from './hooks';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { paths } from '@common/types';
import { CloudDone } from '@mui/icons-material';
import { theme } from '@store';
import { StatusBanner } from '@components';

interface IAssessmentProps {
  token: string;
  assessment_id: string;
  assessment: paths['/assessment/{assessment_id}']['get']['responses']['200']['content']['application/json'];
  handleConfirm: () => void;
}

export const Assessment = ({
  token,
  assessment_id,
  assessment,
  handleConfirm,
}: IAssessmentProps) => {
  const { formMethods, isSavingAssessment, handleChangeField } = useAssessment({
    token,
    assessment_id,
    assessment,
  });

  const { control } = formMethods;

  return (
    <FormProvider {...formMethods}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          pb: '2rem',
        }}
      >
        <Form
          control={control}
          // onSubmit={e => {
          //   return onSubmit(e.event);
          // }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '900px',
            width: '100%',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'sticky',
              top: '80px',
              pt: 3,
              pb: 2,
              mb: 1,
              backgroundColor: theme.palette.common.white,
              zIndex: 3,
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant='h6' component='h2'>
                {assessment?.name || 'Edit assessment'}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 2,
                  position: 'sticky',
                  bottom: 0,
                  paddingY: 1,
                  backgroundColor: theme.palette.common.white,
                  zIndex: 3,
                }}
              >
                {isSavingAssessment ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <CircularProgress
                      size='24px'
                      sx={{ color: 'rgba(0, 0, 0, 0.12)' }}
                    />
                    <Typography sx={{ color: 'text.disabled' }}>
                      {t('general.isSaving')}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <CloudDone
                      sx={{ height: '24px', width: '32px' }}
                      color='disabled'
                    />
                    <Typography sx={{ color: 'text.disabled' }}>
                      {t('general.saved')}
                    </Typography>
                  </Box>
                )}
                <Button
                  size='large'
                  disabled={isSavingAssessment}
                  color='secondary'
                  variant='contained'
                  sx={{ color: theme.palette.common.white }}
                  onClick={handleConfirm}
                >
                  {t('general.confirm')}
                </Button>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'center',
                width: '100%',
              }}
            >
              {assessment?.completed ? (
                <CheckCircleIcon
                  sx={{
                    fontSize: '1.5rem',
                    color: theme.palette.success.main,
                  }}
                />
              ) : (
                <WarningIcon
                  sx={{ fontSize: '1.5rem', color: theme.palette.red.main }}
                />
              )}

              <Typography
                variant='subtitle1'
                sx={{
                  color: assessment?.completed
                    ? theme.palette.success.main
                    : theme.palette.red.main,
                }}
              >
                {assessment?.completed ? 'Completed' : 'Incomplete'}
              </Typography>
            </Box>
          </Box>

          {assessment.warning_text && (
            <StatusBanner variant='warning' text={assessment.warning_text} />
          )}

          <Box
            sx={{
              overflowY: 'scroll',
              display: 'flex',
              justifyContent: 'flex-start',
              flexDirection: 'column',
              width: '100%',
              gap: '1rem',
              paddingTop: 1,
            }}
          >
            {assessment?.requirements.map((req, index) => (
              <RequirementItem
                key={`${req.title} ${index}`}
                {...req}
                // patient_id={patient_id}
                handleChangeField={handleChangeField}
              />
            ))}
          </Box>
        </Form>
      </Box>
    </FormProvider>
  );
};
